/**
 * DESTASH CONDITION CONSTANTS
 * 
 * Defines condition options for each item type when listing for destash.
 * These are community-standard terms that nail enthusiasts recognize.
 */

export interface ConditionOption {
  value: string;
  label: string;
  description?: string;
}

// Polish conditions - most detailed since polish has specific terminology
export const POLISH_CONDITIONS: ConditionOption[] = [
  { value: 'bnib', label: 'BNIB', description: 'Brand New In Box - sealed, never opened' },
  { value: 'bnnu', label: 'BNNU', description: 'Brand New Never Used - opened but never used' },
  { value: 'swatched-wheel', label: 'Swatched on wheel', description: 'Only tested on a nail wheel' },
  { value: 'swatched-1-nail', label: 'Swatched 1 nail', description: 'Tested on one fingernail' },
  { value: 'swatched-1-hand', label: 'Swatched 1 hand', description: 'Tested on 5 nails' },
  { value: 'used-1x', label: 'Used 1x', description: 'One full manicure' },
  { value: 'used-2-3x', label: 'Used 2-3x', description: 'A few manicures' },
  { value: 'used-see-fill', label: 'Used (see fill line)', description: 'Used enough to see the level' },
  { value: 'fill-75', label: '~75% full', description: 'Approximately 75% remaining' },
  { value: 'fill-50', label: '~50% full', description: 'Approximately half remaining' },
  { value: 'fill-25', label: '~25% full', description: 'Approximately 25% remaining' },
  { value: 'well-loved', label: 'Well loved', description: 'Heavily used' },
];

// Stamping plate conditions
export const PLATE_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened, in original packaging' },
  { value: 'new-opened', label: 'New (opened)', description: 'Opened but never used' },
  { value: 'like-new', label: 'Like New', description: 'Used once or twice, no visible wear' },
  { value: 'light-scratches', label: 'Light scratches', description: 'Minor surface scratches, stamps fine' },
  { value: 'visible-wear', label: 'Visible wear', description: 'Noticeable scratches or wear' },
];

// Transfer/Decal conditions
export const TRANSFER_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened' },
  { value: 'opened-complete', label: 'Opened (complete)', description: 'Opened but all pieces intact' },
  { value: 'partial-90', label: '~90% remaining', description: 'A few pieces used' },
  { value: 'partial-75', label: '~75% remaining', description: 'About 75% of sheet/pack remaining' },
  { value: 'partial-50', label: '~50% remaining', description: 'About half remaining' },
  { value: 'partial-25', label: '~25% remaining', description: 'About 25% remaining' },
];

// Embellishment conditions
export const EMBELLISHMENT_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened' },
  { value: 'opened-complete', label: 'Opened (complete)', description: 'Opened but all pieces intact' },
  { value: 'partial-90', label: '~90% remaining', description: 'A few pieces used' },
  { value: 'partial-75', label: '~75% remaining', description: 'About 75% remaining' },
  { value: 'partial-50', label: '~50% remaining', description: 'About half remaining' },
  { value: 'partial-25', label: '~25% remaining', description: 'About 25% remaining' },
];

// Tool conditions
export const TOOL_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened, in original packaging' },
  { value: 'new', label: 'New', description: 'Opened but never used' },
  { value: 'like-new', label: 'Like New', description: 'Used once or twice, excellent condition' },
  { value: 'good', label: 'Good', description: 'Normal wear, fully functional' },
  { value: 'fair', label: 'Fair', description: 'Visible wear but still functional' },
];

// Powder conditions (chrome, aurora, etc.)
export const POWDER_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened' },
  { value: 'bnnu', label: 'BNNU', description: 'Brand New Never Used - opened but never used' },
  { value: 'used-1-2x', label: 'Used 1-2x', description: 'Used once or twice' },
  { value: 'fill-75', label: '~75% full', description: 'Approximately 75% remaining' },
  { value: 'fill-50', label: '~50% full', description: 'Approximately half remaining' },
  { value: 'fill-25', label: '~25% full', description: 'Approximately 25% remaining' },
];

// Acrylic/Builder conditions
export const ACRYLIC_CONDITIONS: ConditionOption[] = [
  { value: 'new-sealed', label: 'New/Sealed', description: 'Never opened' },
  { value: 'bnnu', label: 'BNNU', description: 'Brand New Never Used - opened but never used' },
  { value: 'used-1-2x', label: 'Used 1-2x', description: 'Used once or twice' },
  { value: 'fill-75', label: '~75% full', description: 'Approximately 75% remaining' },
  { value: 'fill-50', label: '~50% full', description: 'Approximately half remaining' },
  { value: 'fill-25', label: '~25% full', description: 'Approximately 25% remaining' },
];

// Helper function to get conditions by item type
export const getConditionsForType = (itemType: string): ConditionOption[] => {
  switch (itemType) {
    case 'polish':
      return POLISH_CONDITIONS;
    case 'plate':
      return PLATE_CONDITIONS;
    case 'transfer':
      return TRANSFER_CONDITIONS;
    case 'embellishment':
      return EMBELLISHMENT_CONDITIONS;
    case 'tool':
      return TOOL_CONDITIONS;
    case 'powder':
      return POWDER_CONDITIONS;
    case 'acrylic':
      return ACRYLIC_CONDITIONS;
    default:
      return POLISH_CONDITIONS; // Fallback
  }
};

// Helper to get label from value
export const getConditionLabel = (itemType: string, value: string): string => {
  const conditions = getConditionsForType(itemType);
  const condition = conditions.find(c => c.value === value);
  return condition?.label || value;
};

// Helper to get description from value
export const getConditionDescription = (itemType: string, value: string): string | undefined => {
  const conditions = getConditionsForType(itemType);
  const condition = conditions.find(c => c.value === value);
  return condition?.description;
};
